import { useState } from 'react';
import { useSensors, useSensor, MouseSensor, TouchSensor } from '@dnd-kit/core';
import { pointerWithin, rectIntersection } from '@dnd-kit/core';
import { localDateToString } from '@/utils/datetime';
import { getWeekDays, getStartOfWeek } from '@/utils/datetime';

const DRAG_SENSORS = {
  mouse: { distance: 8 },
  touch: { delay: 700, tolerance: 8 },
};

// Fields carried on a dragged entry's data payload (see DraggableMealEntry's
// useDraggable data) that fully describe whichever kind of slot it is, so a
// move can be applied without re-fetching the entry. ingredient is carried
// as-is -- it's already shaped {ingredient_id, amount, unit} (or null) from
// the API, matching what the patch body needs, so there's nothing to convert.
const ENTRY_FIELDS = ['entryType', 'recipeId', 'ingredient', 'eventId'];

function entryPatch(entryFields) {
  return {
    entry_type: entryFields.entryType,
    recipe_id: entryFields.recipeId,
    ingredient: entryFields.ingredient ?? null,
    event_id: entryFields.eventId,
  };
}

export function useDragAndDrop({ householdId, currentWeek, weekStartDay, mealPlanEntriesCopy, setMealPlanEntriesCopy, updateMealPlanEntry }) {
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: DRAG_SENSORS.mouse }),
    useSensor(TouchSensor, { activationConstraint: DRAG_SENSORS.touch })
  );

  const collisionDetection = (args) => {
    const pointerIntersections = pointerWithin(args);
    if (pointerIntersections.length > 0) {
      const nextWeekZone = pointerIntersections.find((intersection) => intersection.id === 'next-week-drop-zone');
      if (nextWeekZone) return [nextWeekZone];
      return pointerIntersections;
    }
    return rectIntersection(args);
  };

  const findFirstEmptySlotInNextWeek = (mealType, nextWeekDays) => {
    for (const date of nextWeekDays) {
      const dateString = localDateToString(date);
      const dayEntries = mealPlanEntriesCopy.filter((entry) => {
        const entryDate = localDateToString(new Date(entry.date));
        return entryDate === dateString;
      });
      const entriesForMeal = dayEntries.filter((entry) => entry.meal_type === mealType);
      if (entriesForMeal.length === 0) {
        return { date, mealType };
      }
    }
    return null;
  };

  const handleMoveToNextWeek = async (entryId, entryFields, originalDate, originalMealType) => {
    const nextWeekStart = new Date(currentWeek);
    nextWeekStart.setDate(nextWeekStart.getDate() + 7);
    const nextWeekDays = getWeekDays(getStartOfWeek(nextWeekStart, weekStartDay));
    const slot = findFirstEmptySlotInNextWeek(originalMealType, nextWeekDays);
    if (!slot) return;

    const newDateString = localDateToString(slot.date);
    const originalDateString = localDateToString(originalDate);
    if (newDateString === originalDateString && slot.mealType === originalMealType) return;

    const patch = { date: newDateString, meal_type: slot.mealType, ...entryPatch(entryFields) };

    setMealPlanEntriesCopy(
      mealPlanEntriesCopy.map((entry) => (entry.id === entryId ? { ...entry, ...patch } : entry))
    );

    try {
      await updateMealPlanEntry({ householdId, id: entryId, data: patch });
    } catch (error) {
      console.error('Failed to move meal to next week:', error);
    }
  };

  const handleMoveToCell = async (entryId, entryFields, originalDate, originalMealType, date, mealType) => {
    const newDateString = localDateToString(date);
    const originalDateString = localDateToString(originalDate);
    if (newDateString === originalDateString && mealType === originalMealType) return;

    const patch = { date: newDateString, meal_type: mealType, ...entryPatch(entryFields) };

    setMealPlanEntriesCopy(
      mealPlanEntriesCopy.map((entry) => (entry.id === entryId ? { ...entry, ...patch } : entry))
    );

    try {
      await updateMealPlanEntry({ householdId, id: entryId, data: patch });
    } catch (error) {
      console.error('Failed to move meal:', error);
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const dragData = active.data.current;
    const dropData = over.data.current;
    if (!dragData || !dropData) return;

    const { entryId, originalDate, originalMealType } = dragData;
    const entryFields = Object.fromEntries(ENTRY_FIELDS.map((key) => [key, dragData[key]]));

    if (dropData.isNextWeekZone) {
      await handleMoveToNextWeek(entryId, entryFields, originalDate, originalMealType);
      return;
    }

    const { date, mealType, isEditable } = dropData;
    if (!isEditable) return;

    await handleMoveToCell(entryId, entryFields, originalDate, originalMealType, date, mealType);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  return {
    activeId,
    sensors,
    collisionDetection,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
  };
}
